import React from 'react'
import PropTypes from 'prop-types'
import cssModules from 'react-css-modules'
import MaskedInput from 'react-text-mask'
import { getCurrencySymbol } from 'client/griffin'

import styles from './text_box.less'
import AutosizeTextarea from 'react-textarea-autosize'
import CurrencyTextBox from 'components/common/currency_text_box'
import InputError from 'components/common/input_error'
import { getJSONFromScript } from 'lib/server_params'

import ReactCSSTransitionGroup from 'react-transition-group/CSSTransitionGroup'

/*
	A Note on TextBoxes in Pools
	~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

	There are three kinds of textboxes:
	1. Native/low-level TextBoxes, which require a behavior that fundamentally changes the
	   way that textboxes behave. These include the regular <input>, <textarea>, MaskedInput
		 (from an external lib) and our very own CurrencyTextBox which mimics the behvior found
		 in PayPal.Me (and 8ball).
	2. Regular TextBox, which uses any of the 4 above "native" TextBoxes. It selects which one to use based
	   	on the props it was given. See the "if" statement in the render() function below to understand how.
	3. Higher-order textboxes, which include MoneyTextBox and DateTextBox. These use _this_ component - TextBox
	   to provide input for date or money.

	When programming your own form, you should use textboxes from either the second category (if all you need is
	regular text input) or from the third category (if you need something fancier like date or money amount input).
*/

const TRANSITION_DURATION = 150

const isMobile = !!getJSONFromScript('store').device_info.isMobile

class TextBox extends React.Component {
  static propTypes = {
    multiline: PropTypes.bool,
    title: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxLength: PropTypes.number,
    autoFocus: PropTypes.bool,
    onChange: PropTypes.func,
    onKeyUp: PropTypes.func,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    touched: PropTypes.bool,
    active: PropTypes.bool,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    currencyCode: PropTypes.string,
    decimalDigits: PropTypes.number,
    decimalDot: PropTypes.string,
    removeTrailingZeros: PropTypes.bool,
    type: PropTypes.string,
    styles: PropTypes.object,
    rows: PropTypes.number,
    cols: PropTypes.number,
    mask: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
    pipe: PropTypes.func,
    keepCharPositions: PropTypes.bool,
    guide: PropTypes.bool,
    disableChange: PropTypes.bool,
    dataNemo: PropTypes.string,
    alwaysShowErrors: PropTypes.bool,
    minAmount: PropTypes.number,
    id: PropTypes.string,
    isDisabled: PropTypes.bool,
  }

  static defaultProps = {
    isDisabled: false,
    multiline: false,
    type: 'text',
  }

  componentDidUpdate(prevProps) {
    if (this.props.type === 'date') {
      this.input.defaultValue = ''
    }
  }

  render() {
    let error,
      hasError = false
    if (
      this.props.alwaysShowErrors ||
      (this.props.touched && !this.props.active)
    ) {
      error = this.props.error
      if (error) {
        if (!Array.isArray(error)) {
          error = [error]
        }
        error = error.filter(e => !!e)
        hasError = error.length > 0
      }
    }

    let InputTagName
    let inputProps = {}
    if (this.props.mask) {
      InputTagName = MaskedInput
      inputProps = {
        mask: this.props.mask,
        pipe: this.props.pipe,
        guide: this.props.guide,
        keepCharPositions: this.props.keepCharPositions,
        type: 'text',
        disabled: this.props.disableChange,
      }
    } else if (this.props.multiline) {
      InputTagName = AutosizeTextarea
      inputProps = {
        minRows: this.props.rows,
        cols: this.props.cols,
      }
    } else if (this.props.currencyCode) {
      InputTagName = CurrencyTextBox
      inputProps = {
        currencyCode: this.props.currencyCode,
        removeTrailingZeros: this.props.removeTrailingZeros,
        decimalDigits: this.props.decimalDigits,
        decimalDot: this.props.decimalDot,
        minAmount: this.props.minAmount,
        atmInput: false, // always false for text box
        // Notify devices that the input format in this textbox is numeric.
        // Note that https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
        // specifies that there is a conflict with the spec:
        // > The WHATWG spec lists inputmode, and modern browsers are working towards supporting it.
        // > The W3C HTML 5.2 spec however no longer lists it (i.e. marks it as obsolete).
        // > You should consider the WHATWG definition as correct, until a consensus is reached.
        inputMode: 'decimal',
      }
    } else {
      InputTagName = 'input'
      inputProps = {
        type: this.props.type,
      }
    }

    const errorTransitionNames = {
      enter: this.props.styles['error-enter'],
      enterActive: this.props.styles['error-enter-active'],
      leave: this.props.styles['error-leave'],
      leaveActive: this.props.styles['error-leave-active'],
    }

    const showCurrencySymbol = !!this.props.currencyCode

    let inputStyles = []
    if (!this.props.value && this.props.value !== 0) {
      inputStyles.push('empty')
    }

    if (hasError) {
      inputStyles.push('has-error')
    }

    if (showCurrencySymbol) {
      inputStyles.push('has-currency-symbol')
    }

    return (
      <div styleName="text-box">
        <InputTagName
          styleName={inputStyles.join(' ')}
          data-nemo={this.props.dataNemo}
          placeholder={this.props.placeholder}
          onBlur={this.props.onBlur}
          onFocus={this.props.onFocus}
          onChange={this.props.onChange}
          onKeyUp={this.props.onKeyUp}
          value={this.props.value}
          maxLength={this.props.maxLength}
          autoFocus={this.props.autoFocus && !isMobile}
          ref={input => {
            this.input = input
          }}
          {...inputProps}
          disabled={this.props.isDisabled}
          minAmount={this.props.minAmount}
          aria-label={this.props.title} // can be read by screen reader when the focus is on this textbox
          id={this.props.id || ''}
        />
        <label htmlFor={this.props.id || ''} aria-hidden="true">
          {this.props.title}
        </label>
        {showCurrencySymbol && (
          <span styleName="currency">
            {getCurrencySymbol(this.props.currencyCode)}
          </span>
        )}
        <ReactCSSTransitionGroup
          transitionName={errorTransitionNames}
          transitionEnterTimeout={TRANSITION_DURATION}
          transitionLeaveTimeout={TRANSITION_DURATION}
        >
          {hasError && (
            <div styleName="error-container">
              <InputError errors={error} />
            </div>
          )}
        </ReactCSSTransitionGroup>
      </div>
    )
  }
}

export default cssModules(TextBox, styles, { allowMultiple: true })
